import React, { useCallback, useState, useEffect, useRef } from 'react';
import { RefreshCw, Search, Filter, Paperclip, ChevronDown, ChevronUp, X, ArrowRight } from 'lucide-react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import WorkflowRefLink from '../shared/WorkflowRefLink';
import { formatDateShort } from '../../utils/dateFormat';
import { getDefaultDateRange } from '../../utils/dateRange';
import { API_BASE_URL } from '../../utils/api';

const CPDRecords = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  // Which training category the course files under. 'none' = rows that have
  // no category yet.
  const [filterCategory, setFilterCategory] = useState('all');
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);
  // Certificates kept against a record. The list only carries
  // a count — the file list is fetched when a row is actually opened, so a
  // 2000-row table does not fire 2000 requests nobody asked for.
  // `scope` keeps the two tables apart: the same record shows up both under a
  // participant and in the full list, and without it opening one would silently
  // open the other too.
  const [openCerts, setOpenCerts] = useState(null); // { scope, recordId, files, loading, error }
  // Expanding a participant lists that person's own CPD records in place,
  // the same gesture Training Sessions uses.
  const [expandedEmail, setExpandedEmail] = useState(null);
  // The open row, so collapsing from the bottom of a long panel can bring it
  // back into view instead of dumping the reader mid-table.
  const openParticipantRowRef = useRef(null);

  const toggleCerts = async (scope, recordId) => {
    if (openCerts?.recordId === recordId && openCerts?.scope === scope) { setOpenCerts(null); return; }
    setOpenCerts({ scope, recordId, files: [], loading: true, error: null });
    try {
      const res = await fetch(`${API_BASE_URL}/api/ld/cpd-records/${recordId}/files`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load certificates');
      setOpenCerts({ scope, recordId, files: data.data || [], loading: false, error: null });
    } catch (e) {
      setOpenCerts({ scope, recordId, files: [], loading: false, error: e.message });
    }
  };

  const collapseParticipant = () => {
    const row = openParticipantRowRef.current;
    const offscreen = row ? row.getBoundingClientRect().top < Math.max(0, row.closest('.table-scroll')?.getBoundingClientRect().top ?? 0) : false;
    setExpandedEmail(null);
    setOpenCerts((prev) => (prev?.scope === 'participant' ? null : prev));
    if (offscreen) {
      requestAnimationFrame(() => row.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const toggleParticipant = (email) => {
    if (expandedEmail === email) { collapseParticipant(); return; }
    setExpandedEmail(email);
    setOpenCerts((prev) => (prev?.scope === 'participant' ? null : prev));
  };

  // Esc closes the open participant, the same key that closes every panel here.
  useEffect(() => {
    if (!expandedEmail) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') collapseParticipant(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedEmail]);

  const fetchCPDRecords = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ld/cpd-records`, {
        method: 'GET',
        credentials: 'include', // Include HTTPOnly cookies
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch External CPD records');
      }
      
      const data = await response.json();
      if (data.success) {
        setRecords(data.data);
        setLastUpdated(new Date(data.timestamp));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching CPD records:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCPDRecords();
  }, [fetchCPDRecords]);

  // Get unique positions and courses for filters
  const uniquePositions = ['all', ...new Set(records.map(r => r.position).filter(Boolean))];
  const uniqueCourses = ['all', ...new Set(records.map(r => r.trainingTitle).filter(Boolean))];
  // From the rows themselves, so a retired category with records still shows.
  const uniqueCategories = [...new Set(records.map(r => r.categoryTitle).filter(Boolean))].sort();
  const hasUncategorised = records.some(r => !r.categoryTitle);

  // Parse date helper
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  // Filter and search CPD records
  const filteredRecords = records.filter(record => {
    const matchesSearch =
      record.participantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.participantEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.trainingTitle?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPosition = filterPosition === 'all' || record.position === filterPosition;
    const matchesCourse = filterCourse === 'all' || record.trainingTitle === filterCourse;
    const matchesCategory = filterCategory === 'all'
      || (filterCategory === 'none' ? !record.categoryTitle : record.categoryTitle === filterCategory);

    // Date filtering
    let matchesDate = true;
    if (startDate || endDate) {
      const recordDate = parseDate(record.certificateDate);
      if (recordDate) {
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (end) end.setHours(23, 59, 59, 999);
        
        if (start && recordDate < start) matchesDate = false;
        if (end && recordDate > end) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesPosition && matchesCourse && matchesCategory && matchesDate;
  });

  // Calculate statistics
  const totalRecords = filteredRecords.length;
  const totalHours = filteredRecords.reduce((sum, r) => sum + (r.hours || 0), 0);
  const uniqueParticipants = new Set(filteredRecords.map(r => r.participantEmail)).size;

  // Group by participant. The records themselves ride along so expanding a
  // participant needs no second request — the rows are already in hand, and
  // they honour whatever filters are currently set.
  const participantStats = filteredRecords.reduce((acc, record) => {
    const email = record.participantEmail;
    if (!acc[email]) {
      acc[email] = {
        name: record.participantName,
        position: record.position,
        totalHours: 0,
        courses: new Set(),
        records: []
      };
    }
    acc[email].totalHours += record.hours || 0;
    acc[email].courses.add(record.trainingTitle);
    acc[email].records.push(record);
    return acc;
  }, {});

  const participantRows = Object.entries(participantStats).map(([email, stats]) => ({ email, ...stats }));
  const {
    rows: participantPage,
    sortConfig: participantSortConfig,
    requestSort: requestParticipantSort,
    page: participantPageNo,
    setPage: setParticipantPage,
    total: participantTotal,
  } = usePagedSortableData(participantRows, { key: 'totalHours', direction: 'desc', accessor: r => r.totalHours });

  const {
    rows: recordPage,
    sortConfig: recordSortConfig,
    requestSort: requestRecordSort,
    page: recordPageNo,
    setPage: setRecordPage,
    total: recordTotal,
  } = usePagedSortableData(filteredRecords);

  if (loading && records.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading External CPD records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="text-red-800">
          <h3 className="font-semibold">Error loading External CPD records</h3>
          <p className="text-sm mt-1">{error}</p>
        </div>
        <button
          onClick={fetchCPDRecords}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="External training certificates and professional development"
        lastUpdated={lastUpdated}
        actions={
          <button
            onClick={fetchCPDRecords}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Records</p>
          <p className="text-2xl font-bold text-gray-900">{totalRecords}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Total Hours</p>
          <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(1)}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Unique Participants</p>
          <p className="text-2xl font-bold text-gray-900">{uniqueParticipants}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <p className="text-sm text-gray-600">Avg Hours / Participant</p>
          <p className="text-2xl font-bold text-gray-900">
            {uniqueParticipants > 0 ? (totalHours / uniqueParticipants).toFixed(1) : '0.0'}
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or course..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterPosition}
              onChange={(e) => setFilterPosition(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              {uniquePositions.map(pos => (
                <option key={pos} value={pos}>
                  {pos === 'all' ? 'All Designations' : pos}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterCourse}
              onChange={(e) => setFilterCourse(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              <option value="all">All Courses</option>
              {uniqueCourses.filter(c => c !== 'all').sort().map(course => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              <option value="all">All Categories</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
              {hasUncategorised && <option value="none">Uncategorised</option>}
            </select>
          </div>

        </div>

        {/* Certificate-date range on its own row: as two unlabelled boxes in the
            filter grid the pair got split across rows and nothing said which end
            was which (`placeholder` does nothing on <input type="date">). */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-sm font-medium text-gray-700">
            Certificate Date
          </span>
          <label htmlFor="cpd-date-from" className="text-sm text-gray-600">From</label>
          <input
            id="cpd-date-from"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <ArrowRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
          <label htmlFor="cpd-date-to" className="text-sm text-gray-600">To</label>
          <input
            id="cpd-date-to"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {(startDate || endDate || searchTerm || filterPosition !== 'all' || filterCourse !== 'all' || filterCategory !== 'all') && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">Showing {filteredRecords.length} of {records.length} records</span>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterPosition('all');
                setFilterCourse('all');
                setFilterCategory('all');
                setStartDate('');
                setEndDate('');
              }}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Participant Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Participant Summary</h3>
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={participantSortConfig} onSort={requestParticipantSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Designation" sortKey="position" sortConfig={participantSortConfig} onSort={requestParticipantSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Total Hours" sortKey="totalHours" sortConfig={participantSortConfig} onSort={requestParticipantSort} accessor={r => r.totalHours} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Courses Completed" sortKey="courses" sortConfig={participantSortConfig} onSort={requestParticipantSort} accessor={r => r.courses.size} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="" sortKey="" sortable={false} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {participantPage.map((stats) => {
                const isOpen = expandedEmail === stats.email;
                // Newest certificate first — the same reading order the full
                // records table opens on.
                const ownRecords = [...stats.records].sort(
                  (a, b) => (parseDate(b.certificateDate)?.getTime() || 0) - (parseDate(a.certificateDate)?.getTime() || 0)
                );
                return (
                <React.Fragment key={stats.email}>
                <tr
                  ref={isOpen ? openParticipantRowRef : null}
                  className={`cursor-pointer transition-colors ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  onClick={() => toggleParticipant(stats.email)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleParticipant(stats.email); }
                  }}
                >
                  <td className={`px-6 py-4 whitespace-nowrap border-l-4 ${isOpen ? 'border-blue-600' : 'border-transparent'}`}>
                    <div className="text-sm font-medium text-gray-900">{stats.name}</div>
                    <div className="text-sm text-gray-500">{stats.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {stats.position}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {stats.totalHours.toFixed(1)} hrs
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {stats.courses.size}
                  </td>
                  {/* A worded control, not a bare chevron — same as Training
                      Sessions, where a lone chevron read as decoration. */}
                  <td className="px-6 py-4 text-right">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
                        isOpen
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'bg-white border-gray-300 text-gray-600'
                      }`}
                    >
                      {isOpen ? (
                        <>Close <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Records <ChevronDown className="w-4 h-4" /></>
                      )}
                    </span>
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={5} className="p-0 bg-blue-50/40 border-l-4 border-blue-600">
                      <div className="px-6 py-4 space-y-4">
                        <div className="flex items-start justify-between gap-3">
                          {/* Name, count and hours as one middot-separated
                              line would read as a run-on string (same as the
                              identical header in Mandatory Compliance).
                              A heading plus labelled pills instead. */}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">External CPD Records</p>
                            <h4 className="mt-1 text-base font-semibold text-gray-900 truncate">{stats.name}</h4>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs text-gray-600">
                                <span className="font-semibold text-gray-900 tabular-nums">{ownRecords.length}</span>
                                record{ownRecords.length === 1 ? '' : 's'}
                              </span>
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs text-gray-600">
                                <span className="font-semibold text-gray-900 tabular-nums">{stats.totalHours.toFixed(1)}</span>
                                hrs total
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); collapseParticipant(); }}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors"
                          >
                            <X className="w-4 h-4" /> Close
                            <span className="text-xs text-gray-500">Esc</span>
                          </button>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-blue-200 bg-white">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certificate</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {ownRecords.map((record, idx) => {
                                const certsOpen = openCerts?.scope === 'participant' && openCerts?.recordId === record.id;
                                return (
                                <React.Fragment key={record.id ?? idx}>
                                <tr className="hover:bg-gray-50">
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    {formatDateShort(record.certificateDate)}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {record.trainingTitle}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                    {record.categoryTitle || <span className="text-gray-400">—</span>}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">
                                    {record.hours != null ? Number(record.hours).toFixed(2) : ''}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    {record.fileCount > 0 ? (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleCerts('participant', record.id); }}
                                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                                        aria-expanded={certsOpen}
                                      >
                                        <Paperclip className="w-4 h-4" />
                                        {record.fileCount}
                                      </button>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>
                                {certsOpen && (
                                  <tr className="bg-blue-50/40">
                                    <td colSpan={5} className="px-4 py-2">
                                      {openCerts.loading && <span className="text-sm text-gray-500">Loading certificates…</span>}
                                      {openCerts.error && <span className="text-sm text-red-600">{openCerts.error}</span>}
                                      {!openCerts.loading && !openCerts.error && (
                                        <ul className="space-y-1">
                                          {openCerts.files.map(f => (
                                            <li key={f.id} className="flex items-center gap-2 text-sm">
                                              <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                                              <a
                                                href={`${API_BASE_URL}/api/ld/cpd-records/${record.id}/files/${f.id}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-blue-600 hover:underline"
                                              >
                                                {f.originalName}
                                              </a>
                                              {f.sourceRequestId && (
                                                <span className="text-xs text-gray-500">from declaration <WorkflowRefLink refNo={`CPD-${f.sourceRequestId}`} /></span>
                                              )}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </td>
                                  </tr>
                                )}
                                </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Second way out, for a participant with a long list. */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); collapseParticipant(); }}
                          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-blue-300 bg-white/60 text-sm font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                        >
                          <ChevronUp className="w-4 h-4" /> Collapse This Participant
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager page={participantPageNo} setPage={setParticipantPage} total={participantTotal} />
      </div>

      {/* Detailed Records Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">All External CPD Records ({filteredRecords.length})</h3>
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Date" sortKey="certificateDate" sortConfig={recordSortConfig} onSort={requestRecordSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Participant" sortKey="participantName" sortConfig={recordSortConfig} onSort={requestRecordSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Designation" sortKey="position" sortConfig={recordSortConfig} onSort={requestRecordSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Course" sortKey="course" sortConfig={recordSortConfig} onSort={requestRecordSort} accessor={r => r.trainingTitle} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Category" sortKey="categoryTitle" sortConfig={recordSortConfig} onSort={requestRecordSort} accessor={r => r.categoryTitle || ''} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Hours" sortKey="hours" sortConfig={recordSortConfig} onSort={requestRecordSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Certificate" sortKey="fileCount" sortConfig={recordSortConfig} onSort={requestRecordSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recordPage.map((record, idx) => (
                <React.Fragment key={record.id ?? idx}>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateShort(record.certificateDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{record.participantName}</div>
                    <div className="text-sm text-gray-500">{record.participantEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.position}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="font-medium">{record.trainingTitle}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {record.categoryTitle || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {record.hours != null ? Number(record.hours).toFixed(2) : ''}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {record.fileCount > 0 ? (
                      <button
                        onClick={() => toggleCerts('all', record.id)}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        aria-expanded={openCerts?.scope === 'all' && openCerts?.recordId === record.id}
                      >
                        <Paperclip className="w-4 h-4" />
                        {record.fileCount}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
                {openCerts?.scope === 'all' && openCerts?.recordId === record.id && (
                  <tr className="bg-blue-50/40">
                    <td colSpan={7} className="px-6 py-3">
                      {openCerts.loading && <span className="text-sm text-gray-500">Loading certificates…</span>}
                      {openCerts.error && <span className="text-sm text-red-600">{openCerts.error}</span>}
                      {!openCerts.loading && !openCerts.error && (
                        <ul className="space-y-1">
                          {openCerts.files.map(f => (
                            <li key={f.id} className="flex items-center gap-2 text-sm">
                              <Paperclip className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                              <a
                                href={`${API_BASE_URL}/api/ld/cpd-records/${record.id}/files/${f.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {f.originalName}
                              </a>
                              {f.sourceRequestId && (
                                <span className="text-xs text-gray-500">from declaration <WorkflowRefLink refNo={`CPD-${f.sourceRequestId}`} /></span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager page={recordPageNo} setPage={setRecordPage} total={recordTotal} />
      </div>
    </div>
  );
};

export default CPDRecords;
